/**
 * Zapamätanie priradenia v importe z TopSolidu (KOLO 204).
 *
 * User: *„keď dávam import a zaškrtávaš, že zapamätať, tak to sprav inak,
 * lebo môžem omylom ťuknúť… a keď už importujem niečo, čo som mal
 * zapamätané, napíšeš len zapamätané a neukážeš, čo tam pod tým je."*
 *
 * Do KOLA 204 bola fajka v stĺpci Akcie bez nápisu (holý štvorček vedľa
 * ikon) a druhá vo výberovom okne. Teraz je jedna, v riadku, s nápisom
 * „Zapamätať" / „Zapamätá sa", a nad tabuľkami veta, čo robí. Zapamätaný
 * riadok ukáže kód a meno priamo — server ich posiela v analýze
 * (`naucene_kod`, `naucene_nazov`).
 */
import { Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { odkazNaNastavenia } from "@/components/nastavenia/kategorie";
import type { StavPolozky } from "@/components/import/VyberPolozkyDialog";
import type { MapovaniePolozka } from "@/api/hooks/useTopsolidRaw";
import { cn } from "@/lib/utils";

/** V riadku je presne to, čo appka mala zapamätané. */
export function jeZapamatane(p: MapovaniePolozka, stav: StavPolozky): boolean {
  if (stav.zmazat) return p.naucene_zmazat;
  return stav.material_id !== null && stav.material_id === p.naucene_material_id;
}

/**
 * Je čo zapamätať? Len ručná voľba alebo „nenahrávať", ktoré pamäť ešte
 * nemá — prázdny riadok by zapamätal „nič" a automatickú zhodu appka
 * nájde aj nabudúce.
 */
export function smieZapamatat(p: MapovaniePolozka, stav: StavPolozky): boolean {
  if (!stav.zmazat && stav.material_id === null) return false;
  return !jeZapamatane(p, stav);
}

/** Čo by prepísalo zapamätanie — do tooltipu, aby sa to nestalo potichu. */
function zapamatanePopis(p: MapovaniePolozka): string | null {
  if (p.naucene_zmazat) return "nenahrávať";
  if (p.naucene_material_id) return p.naucene_kod || p.naucene_nazov || "inú položku";
  return null;
}

/**
 * V riadku je to, čo majú jeho dielce v aktualizovanej zákazke (KOLO 217).
 * User: *„keď už dokončím import a chcem niečo pridať… nechcem znova
 * vypĺňať veci"* — aktualizácia predvyplní priradenia zo zákazky.
 */
export function jeZoZakazky(p: MapovaniePolozka, stav: StavPolozky): boolean {
  return (
    !stav.zmazat &&
    stav.material_id !== null &&
    stav.material_id === (p.zakazka_material_id ?? null)
  );
}

/** Štítok pri hodnote prevzatej z aktualizovanej zákazky. */
export function ZoZakazkyStitok(): JSX.Element {
  return (
    <Badge
      variant="secondary"
      className="shrink-0"
      title="Toto majú dielce v zákazke teraz — zmeniť sa dá klikom"
    >
      zo zákazky
    </Badge>
  );
}

/** Štítok pri hodnote, ktorá prišla z pamäte. */
export function ZapamataneStitok(): JSX.Element {
  return (
    <Badge
      variant="secondary"
      className="shrink-0"
      title="Vybraté zo zapamätaných priradení — zmeniť sa dá klikom, zmazať v Nastaveniach → TopSolid"
    >
      zapamätané
    </Badge>
  );
}

/**
 * Prepínač s nápisom — nie holá fajka. Kde nie je čo zapamätať, nie je
 * ani prepínač (nedá sa ťuknúť omylom na riadok, ktorý to nepotrebuje).
 */
export function ZapamatatPrepinac({
  polozka,
  stav,
  onZmena,
}: {
  polozka: MapovaniePolozka;
  stav: StavPolozky;
  onZmena: (s: StavPolozky) => void;
}): JSX.Element | null {
  if (!smieZapamatat(polozka, stav)) return null;
  const zapnute = stav.zapamatat;
  const nahradi = zapamatanePopis(polozka);
  return (
    <button
      type="button"
      aria-pressed={zapnute}
      aria-label={`Zapamätať priradenie — ${polozka.popis}`}
      title={
        nahradi
          ? `Nahradí zapamätané ${nahradi} — ďalší import vyberie toto`
          : "Ďalší import s rovnakým názvom vyberie toto samo"
      }
      onClick={() => onZmena({ ...stav, zapamatat: !zapnute })}
      className={cn(
        "inline-flex h-6 shrink-0 items-center gap-1 whitespace-nowrap rounded border px-1.5 text-[11px] font-medium transition-colors",
        zapnute
          ? "border-emerald-600 bg-emerald-50 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200"
          : "border-input text-muted-foreground hover:bg-accent hover:text-foreground",
      )}
    >
      <span
        aria-hidden
        className={cn(
          "flex h-3 w-3 items-center justify-center rounded-sm border",
          zapnute ? "border-emerald-600 bg-emerald-600 text-white" : "border-current",
        )}
      >
        {zapnute ? <Check className="h-2.5 w-2.5" strokeWidth={3} /> : null}
      </span>
      {zapnute ? "Zapamätá sa" : "Zapamätať"}
    </button>
  );
}

/** Jedna veta nad tabuľkami kroku — čo „Zapamätať" robí a kde sa zmaže. */
export function VysvetlenieZapamatania(): JSX.Element {
  return (
    <p className="text-xs text-muted-foreground">
      <span className="font-medium text-foreground">Zapamätať</span> = ďalší import
      s rovnakým názvom (pri doske v rovnakej hrúbke) vyberie toto samo. Zapamätané
      priradenia sa dajú zmazať v{" "}
      <a
        href={odkazNaNastavenia("topsolid")}
        target="_blank"
        rel="noreferrer"
        className="underline underline-offset-2"
      >
        Nastaveniach → TopSolid
      </a>
      .
    </p>
  );
}
